import { LineChart } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import { InsightsMetricsSplitValues } from "@octopusdeploy/portal-routes";
import React, { useEffect } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { InsightsReportLayout } from "~/areas/insights/components/InsightsReportLayout/InsightsReportLayout";
import { ReportMetricsLayout } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import type { ReportMetricsPageProps } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import { useReportFilters } from "~/areas/insights/components/Reports/ReportMetricsLayout/useReportFilters";
import { getChartAxisDateFormatter } from "../../dataTransformation/dateHelpers";
import { getChartReadyInsightsData, getYAxisUnitOfTimeKeys } from "../../dataTransformation/getChartReadyInsightsData";
import { getEvenlySpacedTickArray, getYAxisDataMax } from "../../dataTransformation/getYAxisDataMax";
import { formatDurationInWords } from "../../dataTransformation/stringHelpers";
import { insightsCadenceLookup, insightsCadenceXAxisTickInterval } from "../../insightsCadence";
import { InsightsTooltip } from "../ChartTooltips/InsightsTooltip/InsightsTooltip";
import { DownloadReportCsvButton } from "../DownloadReportCsvButton";
import { InsightsChartCard } from "../InsightsChartCard";
import { LeadTimeTrendTables } from "./TrendTables/LeadTimeTrendTables";
type Props = ReportMetricsPageProps;
export function DeploymentLeadTimeInsightsInner({ report, bffResponse, split, cadence, busy }: Props) {
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const chartData = getChartReadyInsightsData(bffResponse.Series);
    const { LeadTimeKey } = getYAxisUnitOfTimeKeys(chartData);
    const maxYAxisValue = getYAxisDataMax(chartData, `LeadTime.mean.${LeadTimeKey}`);
    const currentGranularity = insightsCadenceLookup[cadence].granularity;
    const dateFormatter = getChartAxisDateFormatter(currentGranularity);
    const showSeries = split !== InsightsMetricsSplitValues.None;
    useEffect(() => {
        dispatchAction("View Deployment Lead Time Insights Report");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (<main>
            <section style={{ padding: space[16] }}>
                <InsightsChartCard height={500}>
                    <LineChart data={chartData} loading={Boolean(busy)} dataKey={`LeadTime.mean.${LeadTimeKey}`} yAxisLabel={`Avg. no. of ${LeadTimeKey.toLowerCase()}`} xAxisDataKey="StartOfInterval" xAxisType="category" yAxisType="number" yAxisDomain={[0, maxYAxisValue]} yAxisTickArray={getEvenlySpacedTickArray(maxYAxisValue)} xTickFormatter={dateFormatter} xAxisTickInterval={insightsCadenceXAxisTickInterval[cadence]} tooltip={<InsightsTooltip showSeries={showSeries} dataKey="LeadTime.mean.duration" valueFormatter={formatDurationInWords} cadence={cadence}/>} showLegend={showSeries}/>
                </InsightsChartCard>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: space[8] }}>
                    <DownloadReportCsvButton report={report}/>
                </div>
            </section>
            <section>
                <LeadTimeTrendTables trends={bffResponse.LeadTime} report={report}/>
            </section>
        </main>);
}
export function DeploymentLeadTimeInsightsPage({ report }: {
    report: InsightsReportResource;
}) {
    const [reportFilter, setReportFilter] = useReportFilters();
    return (<InsightsReportLayout report={report} reportFilter={reportFilter}>
            <ReportMetricsLayout title="Deployment lead time" report={report} trendKey="LeadTime" reportFilter={reportFilter} setReportFilter={setReportFilter}>
                {(props) => <DeploymentLeadTimeInsightsInner {...props}/>}
            </ReportMetricsLayout>
        </InsightsReportLayout>);
}
